import { CardContent } from '@repo/ui/components/ui/card'
import { cn } from '@repo/ui/lib/utils'
import type { ComponentProps, HTMLAttributes, ReactNode } from 'react'
import { PremiumPanel } from '@/components/ui/premium-panel'

export function AppWorkspacePanel({
  children,
  className,
  contentClassName,
  ...props
}: {
  children: ReactNode
  className?: string
  contentClassName?: string
} & ComponentProps<typeof PremiumPanel>) {
  return (
    <PremiumPanel className={cn('app-workspace-panel', className)} {...props}>
      <CardContent className={cn('flex h-full min-h-0 flex-col p-0', contentClassName)}>
        {children}
      </CardContent>
    </PremiumPanel>
  )
}

export function AppWorkspaceHeader({
  eyebrow,
  title,
  description,
  actions,
  className,
  children,
}: {
  eyebrow?: ReactNode
  title?: ReactNode
  description?: ReactNode
  actions?: ReactNode
  className?: string
  children?: ReactNode
}) {
  return (
    <header className={cn('app-workspace-header border-b border-border/70 px-4 py-3', className)}>
      {children ?? (
        <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_auto] xl:items-center">
          <div className="min-w-0">
            {eyebrow ? <div className="app-section-label">{eyebrow}</div> : null}
            {title ? (
              <h2 className="mt-1.5 truncate font-sans text-xl font-semibold tracking-tight text-foreground md:text-2xl">
                {title}
              </h2>
            ) : null}
            {description ? (
              <p className="mt-1.5 truncate text-sm text-muted-foreground">{description}</p>
            ) : null}
          </div>
          {actions ? <div className="min-w-0">{actions}</div> : null}
        </div>
      )}
    </header>
  )
}

function AppWorkspaceSplitFrame({
  children,
  className,
  layout,
}: {
  children: ReactNode
  className?: string
  layout: 'sidebar-detail' | 'main-supporting'
}) {
  return (
    <div
      className={cn(
        'app-workspace-split grid h-full min-h-0 flex-1 gap-0 divide-y divide-border/70 xl:divide-x xl:divide-y-0',
        className
      )}
      data-layout={layout}
    >
      {children}
    </div>
  )
}

export function AppWorkspaceSidebarDetailSplit({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <AppWorkspaceSplitFrame layout="sidebar-detail" className={className}>
      {children}
    </AppWorkspaceSplitFrame>
  )
}

export function AppWorkspaceMainSupportingSplit({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <AppWorkspaceSplitFrame layout="main-supporting" className={className}>
      {children}
    </AppWorkspaceSplitFrame>
  )
}

export function AppWorkspacePane({
  as: Component = 'section',
  children,
  className,
  minHeight = false,
  padded = false,
  scroll = false,
  surface = 'plain',
  ...props
}: {
  as?: 'aside' | 'div' | 'nav' | 'section'
  children: ReactNode
  className?: string
  minHeight?: boolean
  padded?: boolean
  scroll?: boolean
  surface?: 'muted' | 'plain' | 'region'
} & HTMLAttributes<HTMLElement>) {
  return (
    <Component
      className={cn(
        'min-h-0 min-w-0',
        surface === 'region' && 'app-workspace-region',
        surface === 'muted' && 'app-workspace-muted',
        scroll && 'overflow-y-auto',
        padded && 'p-4 md:p-5',
        minHeight && 'min-h-[28rem]',
        className
      )}
      {...props}
    >
      {children}
    </Component>
  )
}

export function AppWorkspacePaneHeader({
  eyebrow,
  description,
  actions,
  className,
}: {
  eyebrow: ReactNode
  description?: ReactNode
  actions?: ReactNode
  className?: string
}) {
  return (
    <div
      className={cn(
        'app-workspace-header flex min-h-[5.5rem] items-center justify-between gap-3 border-b border-border/70 px-4 md:px-5',
        className
      )}
    >
      <div className="min-w-0">
        <div className="app-section-label">{eyebrow}</div>
        {description ? (
          <div className="mt-1 truncate text-sm text-muted-foreground">{description}</div>
        ) : null}
      </div>
      {actions ? <div className="shrink-0">{actions}</div> : null}
    </div>
  )
}

type AppWorkspacePaneSlot = {
  eyebrow?: ReactNode
  description?: ReactNode
  actions?: ReactNode
  children: ReactNode
  contentClassName?: string
}

export function AppWorkspaceTwoPane({
  sidebar,
  detail,
}: {
  sidebar: AppWorkspacePaneSlot
  detail: AppWorkspacePaneSlot
}) {
  const sidebarPane = (
    <AppWorkspacePane as="aside" surface="region" className="flex flex-col">
      <AppWorkspacePaneHeader
        eyebrow={sidebar.eyebrow}
        description={sidebar.description}
        actions={sidebar.actions}
      />
      <div className={cn('min-h-0 flex-1 overflow-y-auto', sidebar.contentClassName)}>
        {sidebar.children}
      </div>
    </AppWorkspacePane>
  )

  const detailPane = (
    <AppWorkspacePane surface="muted" className="flex flex-col">
      {detail.eyebrow ? (
        <AppWorkspacePaneHeader
          eyebrow={detail.eyebrow}
          description={detail.description}
          actions={detail.actions}
        />
      ) : null}
      <div className={cn('min-h-0 flex-1', detail.contentClassName)}>{detail.children}</div>
    </AppWorkspacePane>
  )

  return (
    <AppWorkspaceSidebarDetailSplit>
      {sidebarPane}
      {detailPane}
    </AppWorkspaceSidebarDetailSplit>
  )
}

export function AppWorkspaceContentPanel({
  eyebrow,
  description,
  actions,
  children,
  className,
  contentClassName,
}: {
  eyebrow: ReactNode
  description?: ReactNode
  actions?: ReactNode
  children: ReactNode
  className?: string
  contentClassName?: string
}) {
  return (
    <AppWorkspacePanel className={className}>
      <AppWorkspacePaneHeader eyebrow={eyebrow} description={description} actions={actions} />
      <AppWorkspacePane scroll className={cn('p-4 md:p-5', contentClassName)}>
        {children}
      </AppWorkspacePane>
    </AppWorkspacePanel>
  )
}

export function AppWorkspaceTabbedPanel({
  tabs,
  emptyLabel,
  addControl,
  children,
}: {
  tabs: Array<{
    key: string
    active?: boolean
    children: ReactNode
  }>
  emptyLabel: ReactNode
  addControl?: ReactNode
  children: ReactNode
}) {
  return (
    <AppWorkspacePanel className="app-workspace-tabbed-panel">
      <div className="app-workspace-header flex h-[3.75rem] items-end border-b border-border/70 px-4 pt-3 md:px-5">
        <div className="flex min-w-0 flex-1 items-end gap-1 overflow-x-auto">
          {tabs.length > 0 ? (
            tabs.map((tab) => (
              <AppWorkspaceTabSurface key={tab.key} active={tab.active}>
                {tab.children}
              </AppWorkspaceTabSurface>
            ))
          ) : (
            <div className="flex h-[3.25rem] items-center text-sm text-muted-foreground">
              {emptyLabel}
            </div>
          )}
          {addControl}
        </div>
      </div>
      {children}
    </AppWorkspacePanel>
  )
}

function AppWorkspaceTabSurface({
  active,
  className,
  children,
  ...props
}: {
  active?: boolean
  children: ReactNode
  className?: string
} & HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'app-workspace-tab flex h-[3.25rem] w-56 shrink-0 items-center gap-2 rounded-t-[5px] border-x border-t border-b-2 px-3',
        active
          ? 'app-workspace-tab-active border-b-primary'
          : 'border-x-transparent border-t-transparent border-b-transparent text-muted-foreground hover:border-x-border/50 hover:border-t-border/50',
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
}

export function WorkspaceSectionCard({
  children,
  className,
  ...props
}: {
  children: ReactNode
  className?: string
} & HTMLAttributes<HTMLElement>) {
  return (
    <section
      className={cn('rounded-[6px] border border-border/70 bg-background/70 p-4 md:p-5', className)}
      {...props}
    >
      {children}
    </section>
  )
}

export function WorkspaceInspectorPane({
  children,
  className,
  sticky = true,
}: {
  children: ReactNode
  className?: string
  sticky?: boolean
}) {
  return (
    <aside className={cn('app-workspace-muted min-w-0 overflow-y-auto p-4 md:p-5', className)}>
      <div className={cn('space-y-5', sticky && 'xl:sticky xl:top-4')}>{children}</div>
    </aside>
  )
}

export function WorkspaceMetricStrip({
  metrics,
  className,
}: {
  metrics: Array<{
    key?: string
    icon?: ReactNode
    label: ReactNode
    value: ReactNode
    detail?: ReactNode
  }>
  className?: string
}) {
  return (
    <div className={cn('app-data-card grid gap-0 overflow-hidden rounded-[6px]', className)}>
      {metrics.map((metric, index) => (
        <div
          key={metric.key ?? String(metric.label)}
          className={cn(
            'min-w-[132px] border-border/70 border-b px-4 py-3 last:border-b-0 sm:border-r sm:border-b-0',
            index === metrics.length - 1 && 'sm:border-r-0'
          )}
        >
          <div className="flex items-start gap-3">
            {metric.icon ? (
              <div className="mt-0.5 grid size-7 shrink-0 place-items-center rounded-[4px] border border-border/70 bg-background text-primary">
                {metric.icon}
              </div>
            ) : null}
            <div className="min-w-0">
              <div className="truncate text-sm font-medium text-foreground">{metric.value}</div>
              <div className="mt-0.5 truncate text-xs text-muted-foreground">
                {metric.detail ?? metric.label}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

export function WorkspaceStatusRow({
  label,
  value,
  icon,
  className,
}: {
  label: ReactNode
  value?: ReactNode
  icon?: ReactNode
  className?: string
}) {
  return (
    <div
      className={cn(
        'flex items-center justify-between gap-3 rounded-[4px] border border-border/70 bg-background/70 px-3 py-2.5 text-sm',
        className
      )}
    >
      <span className="min-w-0 truncate text-muted-foreground">{label}</span>
      <span className="flex shrink-0 items-center gap-2 text-right font-medium text-foreground">
        {value}
        {icon}
      </span>
    </div>
  )
}

export function WorkspaceNotice({
  children,
  tone = 'neutral',
  className,
}: {
  children: ReactNode
  tone?: 'neutral' | 'danger' | 'muted'
  className?: string
}) {
  return (
    <div
      className={cn(
        'rounded-[6px] border px-3 py-2.5 text-sm',
        tone === 'neutral' && 'border-border/70 bg-background/70 text-muted-foreground',
        tone === 'muted' && 'border-border/70 bg-muted/25 text-muted-foreground',
        tone === 'danger' && 'border-destructive/40 bg-destructive/10 text-destructive',
        className
      )}
    >
      {children}
    </div>
  )
}
